import { Autocomplete, Chip, InputAdornment } from "@mui/material";
import { useTheme } from "contexts";
import { useState } from "react";
import { Custom } from "components";
import { Validations } from "helpers";
import { SearchRounded } from "@mui/icons-material";
import { StringOrOptionType } from "types";
import { Helpers } from "utils";

type Props = {
  name?: string;
  label?: string;
  value?: StringOrOptionType[]; // Updated
  avatar?: boolean;
  hasError?: boolean;
  freeSolo?: boolean;
  options: StringOrOptionType[]; // Updated
  placeholder?: string;
  onChange?: (value: StringOrOptionType[]) => void; // Updated
  onValidate?: (value: string) => boolean;
};

export const DynamicAutocomplete = ({
  name,
  label,
  options,
  avatar = false,
  hasError = false,
  freeSolo = true,
  value = [],
  placeholder,
  onChange,
  onValidate,
}: Props) => {
  const { theme } = useTheme();

  const [selected, setSelected] = useState<StringOrOptionType[]>(value);
  const [inputValue, setInputValue] = useState<string>("");

  const getOptionLabel = (option: StringOrOptionType): string => {
    if (typeof option === "string") return option;
    return option.label;
  };

  const getOptionValue = (option: StringOrOptionType): string => {
    if (typeof option === "string") return option;
    return option.value as string;
  };

  const handleAdd = (value: StringOrOptionType) => {
    if (!Validations.hasValue(getOptionValue(value))) return;
    if (onValidate ? onValidate(getOptionLabel(value)) : true) {
      const exists = selected.some(
        (item) => getOptionValue(item) === getOptionValue(value)
      );
      if (!exists) {
        const newItem =
          freeSolo || typeof value === "string" ? value : { label: value };
        const newValue = [...selected, newItem] as StringOrOptionType[];
        setSelected(newValue);
        setInputValue("");
        onChange?.(newValue);
      }
    }
  };

  const handleRemove = (valueToRemove: StringOrOptionType) => {
    const newValue = selected.filter((item) => {
      if (typeof item === "string" && typeof valueToRemove === "string") {
        return item !== valueToRemove;
      }
      if (typeof item !== "string" && typeof valueToRemove !== "string") {
        return item.label !== valueToRemove.label;
      }
      return true;
    });
    setSelected(newValue);
    onChange?.(newValue);
  };

  return (
    <Autocomplete
      multiple
      freeSolo={freeSolo}
      options={options.filter(
        (opt) =>
          !selected.some((sel) => getOptionValue(opt) === getOptionValue(sel))
      )}
      value={selected}
      inputValue={inputValue}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) =>
        getOptionLabel(option) === getOptionLabel(value)
      }
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      onChange={(_, newValue) => {
        if (newValue.length > selected.length) {
          handleAdd(newValue[newValue.length - 1]);
        }
      }}
      onBlur={() => handleAdd(inputValue)}
      onKeyDown={(event) => {
        if ((event.key === "Enter" || event.key === "Tab") && inputValue) {
          event.preventDefault();
          handleAdd(inputValue);
        }
      }}
      renderTags={(values, getTagProps) =>
        values.map((item, index) => {
          const email = getOptionLabel(item);
          return (
            <Chip
              {...getTagProps({ index })}
              key={email}
              label={email}
              onDelete={() => handleRemove(item)}
              avatar={
                avatar ? (
                  <Custom.Avatar
                    alt={email}
                    src={Helpers.getUserAvatar(getOptionValue(item))}
                  />
                ) : null
              }
            />
          );
        })
      }
      renderInput={(params) => (
        <Custom.TextField
          {...params}
          name={name}
          label={label}
          error={hasError}
          placeholder={placeholder}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end" sx={{ right: 0 }}>
                <SearchRounded />
              </InputAdornment>
            ),
          }}
          sx={{
            ".MuiOutlinedInput-root": {
              pr: `${theme.spacing.md * theme.spacing.factor}px !important`,
            },
          }}
        />
      )}
    />
  );
};
